import { AfterViewInit, ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Billplan, NextPayment } from '@common/models/plans.model';
import { AnalyticsService, CommerceService, PaymentRenewalAlertService, UtilityService } from '@common/services';
import { PlansService } from '@common/services/plans.service';
import { UserService } from '@modules/auth/services';
import protectedRoutes from '@modules/auth/services/protected-routes';
import { configSection, sideNavItems, sideNavSections } from '@modules/navigation/data/side-nav-dashboard.data';
import { NavigationService } from '@modules/navigation/services';
import { ModalDismissReasons, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Subscription } from 'rxjs';

export const PAYMENT_MODAL_DISMISSED_KEY = 'paymentModalDismissed';

@Component({
    selector: 'sbpro-layout-dashboard',
    templateUrl: './layout-dashboard.component.html',
    styleUrls: ['layout-dashboard.component.scss'],
    encapsulation: ViewEncapsulation.None, // add encaptulation
})
export class LayoutDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() static = false;
    @Input() light = false;
    @HostBinding('class.sidenav-toggled') sideNavHidden = false;
    subscription: Subscription = new Subscription();
    sideNavItems$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    sideNavSections$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    sidenavStyle = 'sidenav-light';
    @ViewChild('modalInitial') modalInitial: TemplateRef<any>;
    @ViewChild('modalInactive') modalInactive: TemplateRef<any>;
    planRejected: Billplan = null;
    constructor(
        public utilityService: UtilityService,
        public navigationService: NavigationService,
        private changeDetectorRef: ChangeDetectorRef,
        private userService: UserService,
        private modalService: NgbModal,
        public plansService: PlansService,
        public analyticsService: AnalyticsService,
        public commerceService: CommerceService,
        private paymentRenewalAlertService: PaymentRenewalAlertService,
    ) {}

    ngOnInit() {
        if (this.light) {
            this.sidenavStyle = 'sidenav-light';
        }
        this.subscription.add(
            this.navigationService.sideNavVisible$().subscribe(isVisible => {
                this.sideNavHidden = !isVisible;
                this.changeDetectorRef.markForCheck();
            }),
        );

        // Validar rutas
        this.sideNavItems$.next({ ...sideNavItems });
        this.sideNavSections$.next([...JSON.parse(JSON.stringify(sideNavSections))]);
        this.validateRoutes();
    }

    ngAfterViewInit() {
        // Subscribe to commerce changes and validate routes when commerce data is available
        this.subscription.add(
            this.commerceService._commerce$.subscribe(commerce => {
                if (commerce && commerce.ownerId) {
                    this.validateRoutes();
                }
            }),
        );

        // Re-validate routes when feature flags update (handles slow network in production)
        this.subscription.add(
            this.analyticsService.flagsUpdated$.subscribe(() => {
                this.validateRoutes();
            }),
        );

        this.subscription.add(
            this.plansService.plansBundle$.subscribe({
                next: nextPlansBundle => {
                    if (nextPlansBundle) {
                        this.getPlans(nextPlansBundle.nextPayment);
                        this.planRejected = nextPlansBundle.rejectedCurrentBill;
                    }
                },
            }),
        );
    }

    getPlans(nextPaymentValue: NextPayment) {
        const plansBundle = this.plansService.plansBundle$.value;
        if (!nextPaymentValue) {
            return;
        }

        if (plansBundle?.statusTenant === 'INACTIVE') {
            this.modalService.dismissAll();
            this.modalService.open(this.modalInactive, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
            return;
        }

        if (this.paymentRenewalAlertService.isAfterGraceDay(plansBundle) && !plansBundle.isInProcess) {
            this.modalService.dismissAll();
            const modalRef = this.modalService.open(this.modalInitial, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
            modalRef.result.then(
                () => this.markPaymentModalDismissedInSession(),
                () => this.markPaymentModalDismissedInSession(),
            );
        }
    }

    validateRoutes() {
        // Copias de los arreglos y objetos
        // Always start from the master definitions to avoid losing items that were previously filtered out
        let sideNavItemsCopy: any = { ...sideNavItems };
        let sideNavSectionsCopy: any = [...JSON.parse(JSON.stringify(sideNavSections))];
        let itemsValidated: any = {};

        // Get user permissions once
        const userPermissions: any = this.userService.user$.getValue().permissions;

        // Get experiment values once
        const isBankProductsEnabled = this.analyticsService.getExperimentVariant('bank-products');

        // Items that are always included (no permissions or feature flags required)
        const alwaysIncludedItems = ['dashboards', 'perfil', 'help'];

        // Ensure config items are always included
        for (const item of configSection.items) {
            itemsValidated[item] = sideNavItemsCopy[item];
        }

        // Validar rutas
        if (sideNavSectionsCopy) {
            for (const section of sideNavSectionsCopy) {
                for (const sectionItems of section.items) {
                    let submenu = sideNavItemsCopy[sectionItems]?.submenu;
                    let link = sideNavItemsCopy[sectionItems]?.link;

                    // Include items that don't need permission checks
                    if (alwaysIncludedItems.includes(sectionItems)) {
                        itemsValidated[sectionItems] = sideNavItemsCopy[sectionItems];
                    }

                    // Créditos piloto: always visible (módulo maneja estado interno)
                    if (sectionItems === 'bankProducts') {
                        itemsValidated[sectionItems] = sideNavItemsCopy[sectionItems];
                    }

                    if (submenu) {
                        for (const items of sideNavItemsCopy[sectionItems].submenu) {
                            const matchs = protectedRoutes.filter(pr => pr.route === items.link);

                            if (matchs.length > 0) {
                                const match: boolean = userPermissions[`${matchs[0].module}`]?.includes(matchs[0]?.permission);

                                if (match) itemsValidated[sectionItems] = { ...sideNavItemsCopy[sectionItems] };
                            }
                        }
                    }

                    if (link) {
                        let hideRute = false;
                        // Estadísticas depende solo del permiso STATISTICS/view-statistics: sin flag de PostHog,
                        // para que una caída de /flags no oculte el módulo a quien tiene acceso.
                        if (link === '/productos-bancarios') {
                            hideRute = !isBankProductsEnabled;
                        }

                        const matchs = protectedRoutes.filter(pr => pr.route === link);

                        if (matchs.length > 0 && !hideRute) {
                            const match: boolean = userPermissions[`${matchs[0].module}`]?.includes(matchs[0]?.permission);

                            if (match) itemsValidated[sectionItems] = { ...sideNavItemsCopy[sectionItems] };
                        }
                    }
                }
            }
        }

        // Validar si hay rutas vacias
        for (const [i, section] of sideNavSectionsCopy.entries()) {
            const items = [];
            for (const sectionItems of section.items) {
                if (itemsValidated[sectionItems]) {
                    items.push(sectionItems);
                }
            }
            if (items.length <= 0) {
                sideNavSectionsCopy.splice(i, 1);
            } else {
                section.items = [...items];
            }
        }
        this.sideNavItems$.next({ ...itemsValidated });
        this.sideNavSections$.next([...sideNavSectionsCopy]);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    closeSideNavIfOpen() {
        this.navigationService.closeSideNavIfOpenInMobile();
    }

    open(content: TemplateRef<any>, modalOptions: NgbModalOptions = {}) {
        this.modalService.open(content, modalOptions).result;
    }

    private isPaymentModalDismissedInSession(): boolean {
        try {
            return typeof window !== 'undefined' && window.sessionStorage?.getItem(PAYMENT_MODAL_DISMISSED_KEY) === 'true';
        } catch {
            return false;
        }
    }

    private markPaymentModalDismissedInSession(): void {
        try {
            if (typeof window !== 'undefined') {
                window.sessionStorage?.setItem(PAYMENT_MODAL_DISMISSED_KEY, 'true');
            }
        } catch {
            // Some browsers/test environments can deny storage access; the modal flow should still work.
        }
    }

    _getDismissReason(reason: unknown): string {
        if (reason === ModalDismissReasons.ESC) {
            return 'by pressing ESC';
        } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
            return 'by clicking on a backdrop';
        } else {
            return `with: ${reason}`;
        }
    }
}
